import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useState, useEffect } from 'react';
import api from '../api';
import { HomeIcon, UsersIcon, BuildingIcon, BellIcon, FileIcon, WalletIcon, ClockIcon, LogoutIcon, MenuIcon, XIcon, ShieldIcon } from './Icons';

function SideItem({ to, icon: Icon, label, badge, end, onNav }) {
  return (
    <NavLink to={to} end={end} onClick={onNav}
      className={({ isActive }) =>
        `flex items-center gap-3 px-4 py-2.5 mx-3 rounded-lg text-[13.5px] font-medium transition-all duration-150 ${
          isActive
            ? 'bg-white/10 text-white border-l-[3px] border-[#fb866d] pl-[13px]'
            : 'text-slate-400 hover:text-white hover:bg-white/5'
        }`
      }>
      <Icon size={17} />
      <span className="flex-1">{label}</span>
      {badge > 0 && (
        <span className="bg-[#fb866d] text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full min-w-[18px] text-center">
          {badge}
        </span>
      )}
    </NavLink>
  );
}

function DisabledItem({ icon: Icon, label }) {
  return (
    <div className="flex items-center gap-3 px-4 py-2.5 mx-3 rounded-lg text-[13.5px] text-slate-600 cursor-not-allowed select-none">
      <Icon size={17} />
      {label}
    </div>
  );
}

function initials(name = '') {
  return name.split(' ').slice(0, 2).map(n => n[0] || '').join('').toUpperCase();
}

const TITLES = {
  '/': 'Dashboard',
  '/employees': 'Employees',
  '/clients': 'Clients & Projects',
  '/compliance': 'Compliance',
  '/timesheets': 'Timesheets',
  '/payroll': 'Payroll',
  '/import': 'Data Import',
  '/users': 'User Management',
};

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [alertCount, setAlertCount] = useState(0);
  const [open, setOpen] = useState(false);

  const isClient = user?.role === 'client';
  const isViewer = user?.role === 'viewer';

  useEffect(() => {
    if (isClient) return; // alerts are admin-only
    api.get('/employees/alerts').then(r => setAlertCount(r.data.length)).catch(() => {});
  }, [isClient]);

  // close drawer on route change
  useEffect(() => { setOpen(false); }, [location.pathname]);

  const close = () => setOpen(false);

  return (
    <div className="flex min-h-screen bg-[#f6f8fb]">
      {/* Mobile overlay */}
      {open && (
        <div className="fixed inset-0 bg-black/40 z-40 lg:hidden" onClick={close} />
      )}

      {/* Sidebar — drawer on mobile, fixed on desktop */}
      <aside className={`w-60 min-h-screen flex flex-col fixed left-0 top-0 bottom-0 z-50 bg-[#0f1b33] transform transition-transform duration-200 lg:translate-x-0 ${
        open ? 'translate-x-0' : '-translate-x-full'
      }`}>
        {/* Logo */}
        <div className="flex items-center gap-3 px-5 py-5 border-b border-white/[0.06]">
          <div className="w-9 h-9 rounded-lg bg-white flex items-center justify-center flex-shrink-0 p-1">
            <img src="/logo.webp" alt="Delta" className="w-full h-full object-contain" />
          </div>
          <div className="flex-1">
            <p className="text-white text-[15px] font-bold leading-tight tracking-wide">DELTA</p>
            <p className="text-slate-500 text-[11px] leading-tight">HR Console</p>
          </div>
          <button onClick={close} className="lg:hidden text-slate-400 hover:text-white p-1">
            <XIcon size={18} />
          </button>
        </div>

        <nav className="flex flex-col pt-4 flex-1 overflow-y-auto">
          {isClient ? (
            <>
              <p className="px-5 pb-2 text-slate-600 text-[10px] font-bold tracking-[1.5px] uppercase">Portal</p>
              <SideItem to="/timesheets" icon={ClockIcon} label="Attendance" />
            </>
          ) : (
            <>
              <p className="px-5 pb-2 text-slate-600 text-[10px] font-bold tracking-[1.5px] uppercase">Main</p>
              <SideItem to="/" end icon={HomeIcon} label="Dashboard" />
              <SideItem to="/employees" icon={UsersIcon} label="Employees" />
              <SideItem to="/clients" icon={BuildingIcon} label="Clients & Projects" />
              <SideItem to="/compliance" icon={BellIcon} label="Compliance" badge={alertCount} />

              <p className="px-5 pt-5 pb-2 text-slate-600 text-[10px] font-bold tracking-[1.5px] uppercase">Operations</p>
              <SideItem to="/timesheets" icon={ClockIcon} label="Timesheets" />
              <SideItem to="/payroll" icon={WalletIcon} label="Payroll" />
              {!isViewer && <SideItem to="/import" icon={FileIcon} label="Data Import" />}
              {!isViewer && (
                <>
                  <p className="px-5 pt-5 pb-2 text-slate-600 text-[10px] font-bold tracking-[1.5px] uppercase">Admin</p>
                  <SideItem to="/users" icon={ShieldIcon} label="Users & Access" />
                </>
              )}

              <p className="px-5 pt-5 pb-2 text-slate-600 text-[10px] font-bold tracking-[1.5px] uppercase">Coming Soon</p>
              <DisabledItem icon={FileIcon} label="Invoicing" />
            </>
          )}
        </nav>

        {/* User */}
        <div className="p-4 mt-auto border-t border-white/[0.06]">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-full bg-[#fb866d] flex items-center justify-center text-xs font-bold text-white flex-shrink-0">
              {initials(user?.name || user?.email)}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-white text-[13px] font-medium truncate">{user?.name || user?.email}</p>
              <p className="text-slate-500 text-[11px]">{isClient ? (user?.project || 'Client') : isViewer ? 'Read Only' : 'Full Access'}</p>
            </div>
            <button onClick={() => { logout(); navigate('/login'); }}
              className="text-slate-500 hover:text-[#fb866d] transition-colors p-1" title="Logout">
              <LogoutIcon size={16} />
            </button>
          </div>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 min-w-0 lg:ml-60 flex flex-col">
        {/* Mobile header */}
        <div className="lg:hidden sticky top-0 z-30 bg-white border-b border-slate-100 px-4 py-3 flex items-center gap-3">
          <button onClick={() => setOpen(true)} className="p-1.5 rounded-lg text-slate-600 hover:bg-slate-100">
            <MenuIcon size={20} />
          </button>
          <span className="text-[15px] font-bold text-slate-800">{TITLES[location.pathname] || 'Delta HR'}</span>
          {alertCount > 0 && (
            <button onClick={() => navigate('/compliance')} className="ml-auto relative p-1.5 text-slate-500">
              <BellIcon size={18} />
              <span className="absolute -top-0.5 -right-0.5 bg-[#fb866d] text-white text-[9px] font-bold px-1 rounded-full min-w-[15px] text-center">
                {alertCount > 99 ? '99+' : alertCount}
              </span>
            </button>
          )}
        </div>

        <main className="flex-1 min-w-0">
          <Outlet />
        </main>
      </div>
    </div>
  );
}